import { useTranslation } from 'react-i18next';

import ScreenContainer from 'components/containers/ScreenContainer';
import CreateIcon from 'components/icons/CreateIcon';
import { useCallback, useEffect, useState } from 'react';
import ModalForm from 'components/ModalForm';
import {
	askUpdateCertificate,
	createCertificate,
	deleteCertificate,
	fetchCertificates,
	updateCertificate,
} from 'lib/models/certificates';
import Alert, { AlertsTypes } from 'components/Alert';
import ConfirmationDialog from 'components/ConfirmationDialog';
import CertificatesList from 'components/CertificatesList';
import { useHistory } from 'react-router-dom';
import { Box, DialogContentText } from '@material-ui/core';

function Certificates() {
	const history = useHistory();
	const { t } = useTranslation();

	const [certificates, setCertificates] = useState<CertificateType[]>([]);

	const [certificate, setCertificate] = useState<CertificateType>({ name: '', id: '' });
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
	const [modifyQuantity, setModifyQuantity] = useState(0);
	const [deleted, setDeleted] = useState(false);

	const [error, setError] = useState('');

	const refetch = useCallback(async () => {
		setLoading(true);
		const response = await fetchCertificates();
		if (response) {
			setCertificates(response);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		refetch();
	}, [refetch]);

	const edit = async (certificate: CertificateType) => {
		const { success, items } = await askUpdateCertificate(certificate);
		if (success) {
			setModifyQuantity(items);
			setCertificate(certificate);
			setOpen(true);
		}
	};

	const onDelete = async (certificate: CertificateType) => {
		const { success, items } = await askUpdateCertificate(certificate);
		if (success) {
			setModifyQuantity(items);
			setConfirmDeleteModal(true);
			setCertificate(certificate);
		}
	};

	const save = async () => {
		setLoading(true);
		try {
			certificate._id ? await updateCertificate(certificate) : await createCertificate(certificate);
			setSuccess(true);
			refetch();
			closeModal();
		} catch (error) {
			console.error(error);
			setError(error && (error as any).message ? (error as any).message : error);
		}
		setLoading(false);
		resetAlert();
	};

	const onUpdate = async (value: string) => {
		setCertificate((prev) => {
			return { ...prev, name: value };
		});
	};

	const confirmDelete = async (confirm: boolean) => {
		if (confirm) {
			try {
				await deleteCertificate(certificate);
				setDeleted(true);
				setConfirmDeleteModal(false);
				refetch();
			} catch (error: any) {
				console.error(error);
				setError(error.message || error);
			}
		} else {
			setCertificate({ name: '', id: '' });
			setConfirmDeleteModal(false);
		}
		resetAlert();
	};

	const closeModal = () => {
		setOpen(false);
		setCertificate({ name: '', id: '' });
		setModifyQuantity(0);
	};

	const resetAlert = () => {
		setTimeout(() => {
			setSuccess(false);
			setError('');
			setDeleted(false);
		}, 3000);
	};

	const dialogDescription =
		modifyQuantity > 0
			? t('certificates:existent_items_on_delete_description')
			: t('certificates:delete_component_description', { quantity: modifyQuantity });
	return (
		<ScreenContainer title={t('general:certificate_plural')}>
			<CreateIcon
				onCreate={() => {
					setOpen(true);
				}}
			/>

			<CertificatesList certificates={certificates} edit={edit} onDelete={onDelete} />

			{confirmDeleteModal && modifyQuantity === 0 && (
				<ConfirmationDialog
					onClose={confirmDelete}
					title={t('certificates:delete_certificate')}
					description={dialogDescription}
				/>
			)}

			{confirmDeleteModal && modifyQuantity > 0 && (
				<ConfirmationDialog
					confirmButton={t('common:cancel')}
					onClose={() => {
						history.push(`/items?certificate=${certificate.name}`);
					}}
					hideCancel={true}
					title={t('system:errorTryingDelete')}
				>
					<Box>
						<DialogContentText>{t('certificates:existent_items_on_delete_title')}.</DialogContentText>
						<DialogContentText>{dialogDescription}</DialogContentText>
					</Box>
				</ConfirmationDialog>
			)}

			{open && (
				<ModalForm
					title={certificate.id ? t('certificates:edit_certificate') : t('certificates:add_certificate')}
					item={certificate}
					onUpdate={onUpdate}
					onSubmit={save}
					onCancel={closeModal}
					description={
						modifyQuantity > 0
							? t('certificates:edit_certificate_description', { quantity: modifyQuantity })
							: undefined
					}
				/>
			)}
			{!loading && success && (
				<Alert
					message={t('common:success')}
					show={success}
					type={AlertsTypes.success}
					onClose={() => setSuccess(false)}
				/>
			)}

			{!loading && deleted && (
				<Alert
					message={t('common:deleted')}
					show={deleted}
					type={AlertsTypes.success}
					onClose={() => setDeleted(false)}
				/>
			)}

			{!loading && error && (
				<Alert message={error} show={!!error} type={AlertsTypes.error} onClose={() => setError('')} />
			)}
		</ScreenContainer>
	);
}

export default Certificates;
